import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTaskDetail } from '../../hooks/useTaskDetail';
import TaskStatusBadge from '../../components/tasks/TaskStatusBadge';
import TaskPriorityIndicator from '../../components/tasks/TaskPriorityIndicator';
import TaskHistoryTimeline from '../../components/tasks/TaskHistoryTimeline';
import TaskActionsPanel from '../../components/tasks/TaskActionsPanel';
import EditTaskModal from '../../components/tasks/EditTaskModal';
import ReassignTaskModal from '../../components/tasks/ReassignTaskModal';
import {
  FiArrowLeft,
  FiEdit,
  FiTrash2,
  FiCalendar,
  FiUser,
  FiClock,
  FiAlertCircle
} from 'react-icons/fi';
import { isTaskOverdue, getDaysUntilDue } from '../../api/tasks';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const TaskDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { task, history, loading, updating, error, updateStatus, reassign, remove, refetch } =
    useTaskDetail(id, { autoFetch: true, fetchHistory: true });

  const isAdmin =
    user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin';
  const canEdit = isAdmin && task && task.assigned_by === user?.userId;
  const canUpdateStatus = task && (isAdmin || task.assigned_to === user?.userId);

  const handleBack = () => {
    navigate('/tasks');
  };

  const handleStatusChange = async (newStatus) => {
    const result = await updateStatus(newStatus);
    if (result.success) {
      // Show success message (you can add toast notification here)
      console.log('Status updated successfully');
    }
  };

  const handleReassign = async (newAssignedTo) => {
    const result = await reassign(newAssignedTo);
    if (result.success) {
      setShowReassignModal(false);
      // Show success message
    }
  };

  const handleDelete = async () => {
    const result = await remove();
    if (result.success) {
      navigate('/tasks');
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (datetime) => {
    if (!datetime) return '';
    const d = new Date(datetime);
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          <p className="text-sm">{error}</p>
        </div>
        <button
          onClick={handleBack}
          className="mt-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <FiArrowLeft className="mr-2" />
          Back to Tasks
        </button>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Task not found</p>
          <button
            onClick={handleBack}
            className="mt-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <FiArrowLeft className="mr-2" />
            Back to Tasks
          </button>
        </div>
      </div>
    );
  }

  const isOverdue = isTaskOverdue(task.due_date, task.due_time, task.status);
  const daysUntilDue = getDaysUntilDue(task.due_date);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft className="mr-2" />
          Back to Tasks
        </button>

        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-lg font-mono text-accent-gold font-semibold">
                {task.task_number}
              </span>
              <TaskStatusBadge status={task.status} size="lg" />
              {isOverdue && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                  <FiAlertCircle className="mr-1 h-4 w-4" />
                  Overdue
                </span>
              )}
            </div>
            <h1 className="text-3xl font-bold text-primary-black mb-2">{task.title}</h1>
            <TaskPriorityIndicator priority={task.priority} showLabel={true} />
          </div>

          {/* Action Buttons */}
          {canEdit && (
            <div className="flex items-center gap-3 ml-4">
              <button
                onClick={() => setShowEditModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-gold"
              >
                <FiEdit className="mr-2 h-4 w-4" />
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <FiTrash2 className="mr-2 h-4 w-4" />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-primary-black mb-4">Description</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{task.description}</p>
          </div>

          {/* Task Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-primary-black mb-4">Details</h2>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Assigned To</dt>
                <dd className="flex items-center text-sm text-primary-black">
                  <FiUser className="mr-2 h-4 w-4 text-gray-400" />
                  <div>
                    <div className="font-medium">{task.assigned_to_name}</div>
                    <div className="text-gray-500">{task.assigned_to_email}</div>
                  </div>
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Created By</dt>
                <dd className="flex items-center text-sm text-primary-black">
                  <FiUser className="mr-2 h-4 w-4 text-gray-400" />
                  <div>
                    <div className="font-medium">{task.assigned_by_name}</div>
                    <div className="text-gray-500">{task.assigned_by_email}</div>
                  </div>
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Due Date</dt>
                <dd className="flex items-center text-sm">
                  <FiCalendar className="mr-2 h-4 w-4 text-gray-400" />
                  <div>
                    <div className={`font-medium ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                      {formatDate(task.due_date)}
                      {task.due_time && ` at ${task.due_time.slice(0, 5)}`}
                    </div>
                    {daysUntilDue !== null && !isOverdue && daysUntilDue <= 7 && (
                      <div className="text-accent-gold text-xs">
                        {daysUntilDue === 0
                          ? 'Due Today'
                          : `${daysUntilDue} day${daysUntilDue > 1 ? 's' : ''} left`}
                      </div>
                    )}
                  </div>
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Created On</dt>
                <dd className="flex items-center text-sm text-primary-black">
                  <FiClock className="mr-2 h-4 w-4 text-gray-400" />
                  {formatDateTime(task.created_at)}
                </dd>
              </div>

              {task.category && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">Category</dt>
                  <dd className="text-sm text-primary-black">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {task.category}
                    </span>
                  </dd>
                </div>
              )}

              {task.completed_at && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">Completed On</dt>
                  <dd className="flex items-center text-sm text-primary-black">
                    <FiClock className="mr-2 h-4 w-4 text-gray-400" />
                    <div>
                      <div>{formatDateTime(task.completed_at)}</div>
                      {task.completed_by_name && (
                        <div className="text-gray-500 text-xs">by {task.completed_by_name}</div>
                      )}
                    </div>
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Task History */}
          {history && history.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-primary-black mb-4">History</h2>
              <TaskHistoryTimeline history={history} />
            </div>
          )}
        </div>

        {/* Sidebar - Actions */}
        <div className="lg:col-span-1">
          {canUpdateStatus && (
            <TaskActionsPanel
              task={task}
              onStatusChange={handleStatusChange}
              onReassign={() => setShowReassignModal(true)}
              isAdmin={isAdmin}
              updating={updating}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <EditTaskModal
          isOpen={showEditModal}
          task={task}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            refetch();
          }}
        />
      )}

      {showReassignModal && (
        <ReassignTaskModal
          isOpen={showReassignModal}
          task={task}
          onClose={() => setShowReassignModal(false)}
          onSuccess={(newAssignedTo) => handleReassign(newAssignedTo)}
        />
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowDeleteConfirm(false)}
            ></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FiAlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Task</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this task? The task will be moved to the
                        recycle bin and can be restored later.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={updating}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {updating ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={updating}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-gold sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDetailPage;
